export type TelegramAiPeerContext = {
  getSelfId: () => unknown;
  getUser: (peerId: string) => any | Promise<any>;
};

export type TelegramAiPeer = {
  version: 1;
  kind: 'user';
  peerId: string;
  senderId: string;
  isBot: false;
  isOutgoing: false;
};

const officialAccounts = new Set(['777000', '429000', '333000', '42777']);

function userId(value: unknown): string | undefined {
  if(typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : undefined;
  if(typeof value !== 'string' || !/^[1-9][0-9]{0,15}$/.test(value)) return;
  return Number.isSafeInteger(Number(value)) ? value : undefined;
}

/** A positive user ID also identifies bots; require a complete MTProto User. */
export async function getTelegramAiPeer(peerId: unknown, context: TelegramAiPeerContext): Promise<string | null> {
  try {
    const id = userId(peerId);
    const self = userId(context.getSelfId());
    if(!id || !self || id === self || officialAccounts.has(id)) return null;
    const user = await context.getUser(id);
    if(userId(context.getSelfId()) !== self || user?._ !== 'user' || userId(user.id) !== id) return null;
    const flags = user.pFlags;
    if(!flags || typeof flags !== 'object' || Array.isArray(flags)) return null;
    for(const key of ['bot', 'self', 'deleted', 'support', 'min']) {
      if(flags[key] !== undefined && flags[key] !== false) return null;
    }
    return id;
  } catch {
    // Cache misses, unresolved identities and failed lookups never authorize AI.
    return null;
  }
}

/** Resolve only an incoming ordinary-user private message, never a group sender. */
export async function getTelegramAiMessagePeer(message: any, context: TelegramAiPeerContext): Promise<TelegramAiPeer | null> {
  const id = userId(message?.peerId);
  const sender = userId(message?.fromId);
  if(message?._ !== 'message' || !id || sender !== id ||
    message.peer_id?._ !== 'peerUser' || userId(message.peer_id.user_id) !== id ||
    !message.pFlags || typeof message.pFlags !== 'object' || Array.isArray(message.pFlags) ||
    message.pending || message.pFlags.out || message.pFlags.is_outgoing || message.pFlags.is_scheduled) return null;
  if(message.from_id && (message.from_id._ !== 'peerUser' || userId(message.from_id.user_id) !== id)) return null;
  if(!await getTelegramAiPeer(id, context)) return null;
  return {version: 1, kind: 'user', peerId: id, senderId: sender, isBot: false, isOutgoing: false};
}
